import axios from "axios";

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://localhost:30801",
  withCredentials: true,  // send httpOnly auth cookie automatically
});

let _tenantOverride: number | null = null;
export const setTenantOverride = (id: number | null) => { _tenantOverride = id; };
export const getTenantOverride = () => _tenantOverride;

api.interceptors.request.use(config => {
  if (_tenantOverride !== null) {
    (config.headers as any)["X-Tenant-Override"] = String(_tenantOverride);
  }
  return config;
});

// Dispatch a DOM event for billing/quota errors so any component can react.
// Also handle 401 (session expired) by redirecting to login.
api.interceptors.response.use(
  r => r,
  err => {
    const status = err.response?.status;
    if (status === 401) {
      const p = window.location.pathname;
      const isPublicRoute = p.startsWith("/login") || p.startsWith("/signup") ||
        p.startsWith("/invite") || p.startsWith("/setup") ||
        p.startsWith("/careers") || p.startsWith("/screen") ||
        p.startsWith("/pricing") || p.startsWith("/docs") ||
        p.startsWith("/interview-confirm") || p.startsWith("/interview-availability") ||
        p.startsWith("/offer/") || p === "/";
      if (!isPublicRoute) {
        window.location.href = "/login";
        return Promise.reject(err);
      }
    }
    const detail = err.response?.data?.detail;
    if (detail && typeof detail === "object" && detail.code) {
      window.dispatchEvent(new CustomEvent("billing:error", {
        detail: { code: detail.code, message: detail.message, meter: detail.meter },
      }));
    }
    return Promise.reject(err);
  }
);

// FastAPI's `detail` is a plain string for a raised HTTPException, but an array of
// {type, loc, msg, input} objects for automatic Pydantic request-validation (422) errors.
// Call sites that did `err.response.data.detail || fallback` and passed the result straight
// to toast() would hand it a non-string, which crashes React (minified error #31) instead of
// showing a message — use this instead so a validation failure degrades to readable text.
export function getErrorMessage(err: any, fallback: string): string {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const msgs = detail.map((d: any) => (typeof d?.msg === "string" ? d.msg : null)).filter(Boolean);
    if (msgs.length) return msgs.join("; ");
  } else if (detail && typeof detail === "object" && typeof detail.message === "string") {
    return detail.message;
  }
  return fallback;
}

export const getJobs = () => api.get("/api/jobs/").then(r => r.data);
export const getJob = (id: number) => api.get(`/api/jobs/${id}`).then(r => r.data);
export const generateJob = (form: FormData) => api.post("/api/jobs/generate", form).then(r => r.data);
export const createJobManual = (form: FormData) => api.post("/api/jobs/manual", form).then(r => r.data);
export const updateJob = (id: number, form: FormData) => api.put(`/api/jobs/${id}`, form).then(r => r.data);
export const activateJob = (id: number) => api.put(`/api/jobs/${id}/activate`).then(r => r.data);
export const deactivateJob = (id: number) => api.put(`/api/jobs/${id}/deactivate`).then(r => r.data);
export const deleteJob = (id: number) => api.delete(`/api/jobs/${id}`).then(r => r.data);

// ── Application screening questions (per-job custom fields) ────────────────
export type QuestionFieldType = "text" | "number" | "single_select" | "multi_select" | "yes_no";
export interface JobQuestion {
  id: number;
  job_id: number;
  label: string;
  field_type: QuestionFieldType;
  options: string[];
  required: boolean;
  order_index: number;
}
export const getJobQuestions = (jobId: number) =>
  api.get(`/api/jobs/${jobId}/questions`).then(r => r.data as JobQuestion[]);
export const createJobQuestion = (jobId: number, data: { label: string; field_type: QuestionFieldType; options?: string[]; required?: boolean }) =>
  api.post(`/api/jobs/${jobId}/questions`, data).then(r => r.data as JobQuestion);
export const updateJobQuestion = (jobId: number, questionId: number, data: { label: string; field_type: QuestionFieldType; options?: string[]; required?: boolean }) =>
  api.put(`/api/jobs/${jobId}/questions/${questionId}`, data).then(r => r.data as JobQuestion);
export const deleteJobQuestion = (jobId: number, questionId: number) =>
  api.delete(`/api/jobs/${jobId}/questions/${questionId}`).then(r => r.data);
export const reorderJobQuestions = (jobId: number, orderedIds: number[]) =>
  api.put(`/api/jobs/${jobId}/questions/reorder`, { order: orderedIds }).then(r => r.data);
export const copyJobQuestions = (jobId: number, sourceJobId: number) =>
  api.post(`/api/jobs/${jobId}/questions/copy-from-job`, { source_job_id: sourceJobId }).then(r => r.data as JobQuestion[]);

// ── Career page (public) ───────────────────────────────────────────────────
export const getCareerPage = (slug: string) =>
  api.get(`/api/public/careers/${slug}`).then(r => r.data);
export const submitCareerApplication = (slug: string, jobId: number, form: FormData) =>
  api.post(`/api/public/careers/${slug}/apply/${jobId}`, form).then(r => r.data);
export const getTalentPool = (jobId: number) => api.get(`/api/jobs/${jobId}/talent-pool`).then(r => r.data);
export const getInterviewHub = (status?: string) => api.get("/api/candidates/interviews/hub", { params: status ? { status } : {} }).then(r => r.data);
export const getCandidateNotes = (candidateId: number) => api.get(`/api/candidates/${candidateId}/notes`).then(r => r.data);
export const addCandidateNote = (candidateId: number, body: string) => api.post(`/api/candidates/${candidateId}/notes`, { body }).then(r => r.data);
export const deleteCandidateNote = (candidateId: number, noteId: number) => api.delete(`/api/candidates/${candidateId}/notes/${noteId}`).then(r => r.data);
export const addToJobPipeline = (jobId: number, candidateId: number) =>
  api.post(`/api/jobs/${jobId}/talent-pool/add`, { candidate_id: candidateId }).then(r => r.data);
export const postToLinkedIn = (id: number) => api.post(`/api/linkedin/post/${id}`).then(r => r.data);
export const getLinkedInAuthUrl = () => api.get("/api/linkedin/auth/url").then(r => r.data);
export const getLinkedInUserStatus = () => api.get("/api/linkedin/user-status").then(r => r.data);
export const disconnectLinkedIn = () => api.delete("/api/linkedin/disconnect").then(r => r.data);
export const getIntegrations = () => api.get("/api/settings/integrations").then(r => r.data);

// Superadmin platform configs
export const getPlatformLinkedInConfig = () => api.get("/api/admin/tenants/platform/linkedin-config").then(r => r.data);
export const savePlatformLinkedInConfig = (data: object) => api.put("/api/admin/tenants/platform/linkedin-config", data).then(r => r.data);
export const saveSmtp = (data: object) => api.post("/api/settings/smtp", data).then(r => r.data);
export const testSmtp = () => api.post("/api/settings/smtp/test").then(r => r.data);

export type ScreeningFilterSpec = { min?: number | string; max?: number | string; in?: string[]; contains?: string };
export const getCandidates = (jobId?: number, screeningFilters?: Record<number, ScreeningFilterSpec>) =>
  api.get("/api/candidates/", {
    params: {
      ...(jobId ? { job_id: jobId } : {}),
      ...(screeningFilters && Object.keys(screeningFilters).length ? { screening_filters: JSON.stringify(screeningFilters) } : {}),
    },
  }).then(r => r.data);
export const getCandidate = (id: number) => api.get(`/api/candidates/${id}`).then(r => r.data);
export const getCandidateProfilePdfUrl = (id: number) => `${api.defaults.baseURL}/api/candidates/${id}/profile-pdf`;
export const getTalentPoolGlobal = (params?: { q?: string; recommendation?: string; min_exp?: number; max_exp?: number; has_cv?: boolean }) =>
  api.get("/api/candidates/pool", { params }).then(r => r.data);
export const scoreCandidateForJob = (candidateId: number, jobId: number) =>
  api.post(`/api/candidates/${candidateId}/score-for-job`, { job_id: jobId }).then(r => r.data);
export const quickCreateCandidate = (data: { name: string; email: string; current_role?: string; phone?: string }) =>
  api.post("/api/candidates/quick-create", data).then(r => r.data);
// Parses a Content-Disposition header that may carry both a quoted ASCII
// filename= fallback and an RFC 5987 filename*=UTF-8''<percent-encoded> —
// prefer the UTF-8 one so non-Latin-1 names (Korean, Thai, Chinese, ...)
// aren't dropped to their ASCII-stripped fallback.
function _filenameFromContentDisposition(cd: string, fallback: string): string {
  const star = cd.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
  if (star) {
    try { return decodeURIComponent(star[1].trim()); } catch { /* fall through to plain filename */ }
  }
  const plain = cd.match(/filename\s*=\s*"([^"]*)"/i) || cd.match(/filename\s*=\s*([^;]+)/i);
  return plain ? plain[1].trim() : fallback;
}
function _blobWithFilename(r: any, fallback: string): { blob: Blob; filename: string } {
  const cd = r.headers["content-disposition"] || "";
  return { blob: r.data, filename: _filenameFromContentDisposition(cd, fallback) };
}
// applicationId scopes the download to a specific job's formatted CV (each
// application now has its own AI-curated summary/skills/formatted file) —
// pass it whenever the caller has one (job-scoped views, the candidate panel);
// omit it only for candidate-only contexts like the global Talent Pool, where
// the backend falls back to the candidate's most recent application.
export const downloadFormattedCV = (id: number, applicationId?: number | null) =>
  api.get(`/api/candidates/${id}/cv/formatted`, { responseType: "blob", params: applicationId ? { application_id: applicationId } : {} })
    .then(r => _blobWithFilename(r, `CV_${id}.pdf`));
export const downloadFormattedDocx = (id: number, applicationId?: number | null) =>
  api.get(`/api/candidates/${id}/cv/docx`, { responseType: "blob", params: applicationId ? { application_id: applicationId } : {} })
    .then(r => _blobWithFilename(r, `CV_${id}.docx`));
export const downloadRawCV = (id: number) =>
  api.get(`/api/candidates/${id}/cv/raw`, { responseType: "blob" })
    .then(r => _blobWithFilename(r, `Original_CV_${id}`));
export const updateStatus = (appId: number, status: string, notify = true) =>
  api.put(`/api/candidates/${appId}/status`, null, { params: { status, notify } }).then(r => r.data);
export const regenerateCV = (candidateId: number, applicationId?: number | null) =>
  api.post(`/api/candidates/${candidateId}/cv/regenerate`, null, { params: applicationId ? { application_id: applicationId } : {} }).then(r => r.data);
export const attachCV = (candidateId: number, file: File) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post(`/api/candidates/${candidateId}/cv/attach`, fd).then(r => r.data);
};
export const regenerateAllCVs = () =>
  api.post("/api/candidates/cv/regenerate-all").then(r => r.data);
export const uploadCVManual = (file: File, jobId?: number) => {
  const fd = new FormData();
  fd.append("file", file);
  if (jobId) fd.append("job_id", String(jobId));
  return api.post("/api/candidates/upload-cv", fd).then(r => r.data);
};
export const updateCandidateDetails = (candidateId: number, data: object) =>
  api.patch(`/api/candidates/${candidateId}/details`, data).then(r => r.data);
export const deleteApplication = (applicationId: number) =>
  api.delete(`/api/candidates/application/${applicationId}`).then(r => r.data);
export const deleteCandidate = (candidateId: number) =>
  api.delete(`/api/candidates/${candidateId}`).then(r => r.data);
export const generateInterviewQuestions = (candidateId: number, jobId?: number) =>
  api.post(`/api/candidates/${candidateId}/interview-questions`, { job_id: jobId }).then(r => r.data);
export const compareCandidates = (applicationIds: number[], jobId?: number) =>
  api.post("/api/candidates/compare", { application_ids: applicationIds, job_id: jobId ?? null }).then(r => r.data);
export const generateOutreachEmail = (candidateId: number, jobId?: number, tone?: string) =>
  api.post(`/api/candidates/${candidateId}/outreach-email`, { job_id: jobId, tone }).then(r => r.data);
export const sendOutreachEmail = (candidateId: number, data: object) =>
  api.post(`/api/candidates/${candidateId}/outreach-email/send`, data).then(r => r.data);
export const getOutreachHistory = (candidateId: number) =>
  api.get(`/api/candidates/${candidateId}/outreach-history`).then(r => r.data);

export const getClients = () => api.get("/api/jobs/clients/list").then(r => r.data);
export const createClient = (form: FormData) => api.post("/api/jobs/clients", form).then(r => r.data);
export const updateClient = (id: number, form: FormData) => api.put(`/api/jobs/clients/${id}`, form).then(r => r.data);
export const deleteClient = (id: number) => api.delete(`/api/jobs/clients/${id}`).then(r => r.data);

export const getCurrentTenant = () => api.get("/api/admin/tenants/current").then(r => r.data);
export const updateCurrentTenant = (data: object) => api.patch("/api/admin/tenants/current", data).then(r => r.data);
export const getAllTenants = () => api.get("/api/admin/tenants/").then(r => r.data);
export const toggleTenant = (id: number) => api.patch(`/api/admin/tenants/${id}/toggle`).then(r => r.data);
export const resendTenantSetupLink = (id: number) => api.post(`/api/admin/tenants/${id}/resend-setup-link`).then(r => r.data);

// Module entitlements. Super-admin grants/revokes are DB-only and never touch
// Stripe — see .claude/specs/module-entitlement-foundation.md.
export const getMyModules = () => api.get("/api/settings/modules/me").then(r => r.data);
// Outstanding setup for one module, for its dashboard's setup card. Admin-only —
// every item links to a settings page only admins can open.
export const getModuleSetup = (moduleKey: string) =>
  api.get(`/api/settings/modules/${moduleKey}/setup`).then(r => r.data);
export const getTenantModules = (id: number) => api.get(`/api/admin/tenants/${id}/modules`).then(r => r.data);
export const updateTenantModules = (id: number, data: { grant?: string[]; revoke?: string[] }) =>
  api.post(`/api/admin/tenants/${id}/modules`, data).then(r => r.data);
export const createInvite = (data: object) => api.post("/api/admin/tenants/invite", data).then(r => r.data);
export const revokeInvite = (id: number) => api.delete(`/api/admin/tenants/invite/${id}`).then(r => r.data);
export const resendInvite = (userId: number) => api.post(`/api/admin/tenants/users/${userId}/resend-invite`).then(r => r.data);
export const getAuditLog = (params?: object) => api.get("/api/admin/tenants/audit", { params }).then(r => r.data);
export const getTenantUsers = (tenantId: number) => api.get(`/api/admin/tenants/${tenantId}/users`).then(r => r.data);
export const updateUserRole = (userId: number, role: string) => api.patch(`/api/admin/tenants/users/${userId}/role`, { role }).then(r => r.data);
export const removeUser = (userId: number) => api.delete(`/api/admin/tenants/users/${userId}`).then(r => r.data);

export const getGmailStatus = () => api.get("/api/gmail/status").then(r => r.data);
export const disconnectGmail = () => api.delete("/api/gmail/disconnect").then(r => r.data);
export const checkCalendarScope = () => api.get("/api/gmail/calendar/scope-check").then(r => r.data);
export const generateMeetLink = (data: { summary: string; scheduled_at: string; duration_minutes: number }) =>
  api.post("/api/gmail/meet-link", data).then(r => r.data);
export const getGmailCredentialsStatus = () => api.get("/api/gmail/credentials/status").then(r => r.data);
export const uploadGmailCredentials = (fd: FormData) => api.post("/api/gmail/credentials", fd).then(r => r.data);
export const getGmailAuthUrl = () => api.get("/api/gmail/auth/url").then(r => r.data);
export const logout = () => api.post("/api/auth/logout");

// /api/branding is readable by ALL authenticated users (employees, not just admins)
export const getBranding = () => api.get("/api/branding").then(r => r.data);
// /api/settings/branding is admin-only and returns full settings incl. signatory, cv limits, employee_id_prefix
export const getSettingsBranding = () => api.get("/api/settings/branding").then(r => r.data);
export const saveBranding = (data: object) => api.post("/api/settings/branding", data).then(r => r.data);
export const getBrandingLogoUrl = () => `${api.defaults.baseURL}/api/branding/logo?t=${Date.now()}`;
export const getLocale = () => api.get("/api/settings/locale").then(r => r.data);
export const saveLocale = (data: object) => api.post("/api/settings/locale", data).then(r => r.data);
export const getCurrencies    = () => api.get("/api/settings/currencies").then(r => r.data);
export const getAiUsage       = () => api.get("/api/settings/ai-usage").then(r => r.data);
export const updateAiQuota    = (quota: number) => api.put("/api/settings/ai-quota", { quota }).then(r => r.data);
export const getTenantAiConfigs = () => api.get("/api/admin/tenants/ai-config").then(r => r.data);
export const updateTenantAiConfig = (tenantId: number, data: object) =>
  api.put(`/api/admin/tenants/${tenantId}/ai-config`, data).then(r => r.data);
export const getTenantBilling = () => api.get("/api/admin/tenants/billing").then(r => r.data);
export const getPlatformAuditLog = (params: { limit?: number; offset?: number; action?: string; tenant_id?: number }) =>
  api.get("/api/admin/tenants/platform-audit", { params }).then(r => r.data);
export const updateTenantPlan = (tenantId: number, data: object) =>
  api.put(`/api/admin/tenants/${tenantId}/plan`, data).then(r => r.data);
export const getBillingUsage = () => api.get("/api/billing/usage").then(r => r.data);
export const getOnboardingChecklist = () => api.get("/api/auth/onboarding/checklist").then(r => r.data);
export const getStripePlans = () => api.get("/api/stripe/plans").then(r => r.data);
export const createStripeCheckoutSession = (data: { plan: string; billing_period: "monthly" | "annual" }) =>
  api.post("/api/stripe/create-checkout-session", data).then(r => r.data);
export const createStripePortalSession = () =>
  api.post("/api/stripe/create-portal-session").then(r => r.data);
export const getModuleCommerce = () => api.get("/api/stripe/modules").then(r => r.data);
export const addStripeModule = (module_key: string) =>
  api.post("/api/stripe/modules/add", { module_key }).then(r => r.data);
export const removeStripeModule = (module_key: string) =>
  api.post("/api/stripe/modules/remove", { module_key }).then(r => r.data);
export const getBillingInvoices = () => api.get("/api/billing/invoices").then(r => r.data);
export const submitRegistrationRequest = (data: { company_name: string; domain: string; contact_email: string; message?: string }) =>
  api.post("/api/auth/register-request", data).then(r => r.data);
export const getBillingTrends = (days: number = 90) =>
  api.get("/api/admin/tenants/billing/trends", { params: { days } }).then(r => r.data);
export const getBillingAnomalies = (status: "open" | "resolved" = "open") =>
  api.get("/api/admin/tenants/billing/anomalies", { params: { status } }).then(r => r.data);
export const resolveBillingAnomaly = (anomalyId: number) =>
  api.post(`/api/admin/tenants/billing/anomalies/${anomalyId}/resolve`).then(r => r.data);
export const listManualPayments = (tenantId: number) =>
  api.get(`/api/admin/tenants/${tenantId}/manual-payments`).then(r => r.data);
export const logManualPayment = (tenantId: number, data: { amount_usd: number; paid_at?: string; method?: string; note?: string }) =>
  api.post(`/api/admin/tenants/${tenantId}/manual-payments`, data).then(r => r.data);
export const getTenantCurrency = () => api.get("/api/settings/currency").then(r => r.data);
export const updateUserProfile = (userId: number, data: object) =>
  api.patch(`/api/admin/tenants/users/${userId}/profile`, data).then(r => r.data);
export const assignManager = (userId: number, managerId: number | null) =>
  api.patch(`/api/admin/tenants/users/${userId}/manager`, { manager_id: managerId }).then(r => r.data);

// ── Leaves ────────────────────────────────────────────────────────────────
export const getLeaveTypes = () => api.get("/api/leaves/types").then(r => r.data);
export const createLeaveType = (data: object) => api.post("/api/leaves/types", data).then(r => r.data);
export const updateLeaveType = (id: number, data: object) => api.put(`/api/leaves/types/${id}`, data).then(r => r.data);
export const deleteLeaveType = (id: number) => api.delete(`/api/leaves/types/${id}`).then(r => r.data);

export const getHolidays = (year?: number, location?: string) =>
  api.get("/api/leaves/holidays", { params: { ...(year ? { year } : {}), ...(location ? { location } : {}) } }).then(r => r.data);
export const getHolidayLocations = () => api.get("/api/leaves/holidays/locations").then(r => r.data);
export const addHoliday = (data: object) => api.post("/api/leaves/holidays", data).then(r => r.data);
export const deleteHoliday = (id: number) => api.delete(`/api/leaves/holidays/${id}`).then(r => r.data);

export const getMyLeaveBalance = (year?: number) =>
  api.get("/api/leaves/balance", { params: year ? { year } : {} }).then(r => r.data);
export const getUserLeaveBalance = (userId: number, year?: number) =>
  api.get(`/api/leaves/balance/user/${userId}`, { params: year ? { year } : {} }).then(r => r.data);

export const getLeaveTeamSummary = (year?: number) =>
  api.get("/api/leaves/team", { params: year ? { year } : {} }).then(r => r.data);

export const getTeamLeaveRequests = () =>
  api.get("/api/leaves/requests/team").then(r => r.data);

export const getLeaveRequests = (params?: { status?: string; user_id?: number; mine?: boolean }) =>
  api.get("/api/leaves/requests", { params }).then(r => r.data);
export const submitLeaveRequest = (data: object) => api.post("/api/leaves/requests", data).then(r => r.data);
export const editLeaveRequest = (id: number, data: object) => api.put(`/api/leaves/requests/${id}/edit`, data).then(r => r.data);
export const approveLeaveRequest = (id: number) => api.put(`/api/leaves/requests/${id}/approve`).then(r => r.data);
export const rejectLeaveRequest = (id: number, reason?: string) =>
  api.put(`/api/leaves/requests/${id}/reject`, { reason }).then(r => r.data);
export const cancelLeaveRequest = (id: number) => api.put(`/api/leaves/requests/${id}/cancel`).then(r => r.data);

export const overrideLeaveBalance = (data: object) => api.patch("/api/leaves/balance/override", data).then(r => r.data);
export const revertLeaveBalance = (balanceId: number) => api.delete(`/api/leaves/balance/override/${balanceId}`).then(r => r.data);
export const getAllLeaveBalances = (year?: number) =>
  api.get("/api/leaves/balances", { params: year ? { year } : {} }).then(r => r.data);

export const earnCompOff = (data: object) => api.post("/api/leaves/comp-off/earn", data).then(r => r.data);
export const getCompOffEarnings = (userId?: number) =>
  api.get("/api/leaves/comp-off", { params: userId ? { user_id: userId } : {} }).then(r => r.data);

// ── Payroll ───────────────────────────────────────────────────────────────
export const getPayrollEmployees = () => api.get("/api/payroll/employees").then(r => r.data);
export const getSalarySlips = (params?: { user_id?: number; year?: number; month?: number }) =>
  api.get("/api/payroll/slips", { params }).then(r => r.data);
export const getMySlips = () => api.get("/api/payroll/slips/my").then(r => r.data);
export const getSlip = (id: number) => api.get(`/api/payroll/slips/${id}`).then(r => r.data);
export const createSalarySlip = (data: object) => api.post("/api/payroll/slips", data).then(r => r.data);
export const runPayroll = (data: object) => api.post("/api/payroll/run", data).then(r => r.data);
export const updateSlip = (id: number, data: object) => api.put(`/api/payroll/slips/${id}`, data).then(r => r.data);
export const issueSlip = (id: number) => api.put(`/api/payroll/slips/${id}/issue`).then(r => r.data);
export const issueAllSlips = (month: number, year: number) =>
  api.post("/api/payroll/slips/issue-all", { month, year }).then(r => r.data);
export const voidSlip = (id: number) => api.put(`/api/payroll/slips/${id}/void`).then(r => r.data);
export const markSlipPaid = (id: number, data: { payment_date?: string; payment_reference?: string }) =>
  api.put(`/api/payroll/slips/${id}/mark-paid`, data).then(r => r.data);
export const deleteSlip = (id: number) => api.delete(`/api/payroll/slips/${id}`).then(r => r.data);
export const downloadSlipPdf = (id: number) =>
  api.get(`/api/payroll/slips/${id}/pdf`, { responseType: "blob" })
    .then(r => _blobWithFilename(r, `salary_slip_${id}.pdf`));
// Salary master (Gap 1)
export const getSalaryStructure = (userId: number) =>
  api.get(`/api/payroll/employees/${userId}/salary`).then(r => r.data);
export const upsertSalaryStructure = (userId: number, data: object) =>
  api.put(`/api/payroll/employees/${userId}/salary`, data).then(r => r.data);
export const getSalaryStructureHistory = (userId: number) =>
  api.get(`/api/payroll/employees/${userId}/salary/history`).then(r => r.data);
// Worked-days calculator (Gap 3)
export const getWorkedDays = (userId: number, month: number, year: number) =>
  api.get(`/api/payroll/employees/${userId}/worked-days`, { params: { month, year } }).then(r => r.data);
// Tax estimator (Gap 9)
export const estimateTax = (userId: number, month: number, year: number) =>
  api.get(`/api/payroll/employees/${userId}/estimate-tax`, { params: { month, year } }).then(r => r.data);
// Payroll settings (Gap 7)
export const getPayrollSettings = () => api.get("/api/payroll/settings").then(r => r.data);
export const updatePayrollSettings = (data: object) => api.put("/api/payroll/settings", data).then(r => r.data);

// ── Invoices ──────────────────────────────────────────────────────────────
export const getInvoices = (params?: { status?: string; client_id?: number; overdue?: boolean }) =>
  api.get("/api/invoices/", { params }).then(r => r.data);
export const duplicateInvoice = (id: number) =>
  api.post(`/api/invoices/${id}/duplicate`).then(r => r.data);
export const getInvoice = (id: number) => api.get(`/api/invoices/${id}`).then(r => r.data);
export const createInvoice = (data: object) => api.post("/api/invoices/", data).then(r => r.data);
export const updateInvoice = (id: number, data: object) => api.put(`/api/invoices/${id}`, data).then(r => r.data);
export const updateInvoiceStatus = (id: number, status: string) =>
  api.put(`/api/invoices/${id}/status`, { status }).then(r => r.data);
export const voidInvoice = (id: number) => api.post(`/api/invoices/${id}/void`).then(r => r.data);
export const downloadInvoicePdf = (id: number) =>
  api.get(`/api/invoices/${id}/pdf`, { responseType: "blob" })
    .then(r => _blobWithFilename(r, `invoice_${id}.pdf`));
export const downloadInvoiceWhtCertificate = (id: number) =>
  api.get(`/api/invoices/${id}/wht-certificate`, { responseType: "blob" })
    .then(r => _blobWithFilename(r, `wht_certificate_invoice_${id}.pdf`));

// ── Debit / Credit Notes (Thailand AR) ──────────────────────────────────────
export const getDebitCreditNotes = (params?: { type?: string; status?: string; reference_invoice_id?: number }) =>
  api.get("/api/debit-credit-notes/", { params }).then(r => r.data);
export const getDebitCreditNote = (id: number) => api.get(`/api/debit-credit-notes/${id}`).then(r => r.data);
export const createDebitCreditNote = (data: object) => api.post("/api/debit-credit-notes/", data).then(r => r.data);
export const updateDebitCreditNote = (id: number, data: object) => api.put(`/api/debit-credit-notes/${id}`, data).then(r => r.data);
export const issueDebitCreditNote = (id: number) => api.post(`/api/debit-credit-notes/${id}/issue`).then(r => r.data);
export const voidDebitCreditNote = (id: number) => api.post(`/api/debit-credit-notes/${id}/void`).then(r => r.data);
export const downloadDebitCreditNotePdf = (id: number) =>
  api.get(`/api/debit-credit-notes/${id}/pdf`, { responseType: "blob" })
    .then(r => _blobWithFilename(r, `note_${id}.pdf`));
export const exportDebitCreditNotesCsv = (params?: { type?: string; status?: string }) =>
  api.get("/api/debit-credit-notes/export-csv", { params, responseType: "blob" })
    .then(r => _blobWithFilename(r, "debit_credit_notes.csv"));

// ── Thai sales documents (unified) ──────────────────────────────────────────
export const getSalesDocs = (params?: { doc_type?: string; status?: string; client_id?: number }) =>
  api.get("/api/sales-docs/", { params }).then(r => r.data);
export const getSalesDoc = (id: number) => api.get(`/api/sales-docs/${id}`).then(r => r.data);
export const createSalesDoc = (data: object) => api.post("/api/sales-docs/", data).then(r => r.data);
export const updateSalesDoc = (id: number, data: object) => api.put(`/api/sales-docs/${id}`, data).then(r => r.data);
export const issueSalesDoc = (id: number) => api.post(`/api/sales-docs/${id}/issue`).then(r => r.data);
export const approveSalesDoc = (id: number) => api.post(`/api/sales-docs/${id}/approve`).then(r => r.data);
export const convertSalesDoc = (id: number, targetType?: string) => api.post(`/api/sales-docs/${id}/convert`, targetType ? { target_type: targetType } : {}).then(r => r.data);
export const markSalesDocPaid = (id: number) => api.post(`/api/sales-docs/${id}/mark-paid`).then(r => r.data);
export const issueCreditNote = (id: number, data?: object) =>
  api.post(`/api/sales-docs/${id}/credit-note`, data || {}).then(r => r.data);
export const issueDebitNote = (id: number, data?: object) =>
  api.post(`/api/sales-docs/${id}/debit-note`, data || {}).then(r => r.data);
export const voidSalesDoc = (id: number) => api.post(`/api/sales-docs/${id}/void`).then(r => r.data);
export const duplicateSalesDoc = (id: number) => api.post(`/api/sales-docs/${id}/duplicate`).then(r => r.data);
export const deleteSalesDoc = (id: number) => api.delete(`/api/sales-docs/${id}`).then(r => r.data);
export const downloadSalesDocPdf = (id: number, num: string) =>
  api.get(`/api/sales-docs/${id}/pdf`, { responseType: "blob" })
    .then(r => _blobWithFilename(r, `${num || id}.pdf`));
export const downloadSalesDocWhtCertificate = (id: number) =>
  api.get(`/api/sales-docs/${id}/wht-certificate`, { responseType: "blob" })
    .then(r => _blobWithFilename(r, `wht_certificate_${id}.pdf`));
export const getOutputVatReport = (month: number, year: number) =>
  api.get("/api/sales-docs/reports/output-vat", { params: { month, year } }).then(r => r.data);

// ── Products catalog ──────────────────────────────────────────────────────
export const getProducts = (search?: string) =>
  api.get("/api/products/", { params: search ? { search } : {} }).then(r => r.data);
export const createProduct = (data: object) => api.post("/api/products/", data).then(r => r.data);
export const updateProduct = (id: number, data: object) => api.put(`/api/products/${id}`, data).then(r => r.data);
export const deleteProduct = (id: number) => api.delete(`/api/products/${id}`).then(r => r.data);

// ── Employees ─────────────────────────────────────────────────────────────
export const getEmployees = () => api.get("/api/employees/").then(r => r.data);
export const getEmployee = (id: number) => api.get(`/api/employees/${id}`).then(r => r.data);
export const getMyProfile = () => api.get("/api/employees/me").then(r => r.data);
export const getMyReferralLink = () => api.get("/api/employees/me/referral-link").then(r => r.data);
export const updateEmployee = (id: number, data: object) => api.patch(`/api/employees/${id}`, data).then(r => r.data);
export const getEmployeeDocuments = (userId: number) => api.get(`/api/employees/${userId}/documents`).then(r => r.data);
export const uploadEmployeeDocument = (userId: number, form: FormData) =>
  api.post(`/api/employees/${userId}/documents`, form).then(r => r.data);
export const downloadEmployeeDocument = (userId: number, docId: number) =>
  api.get(`/api/employees/${userId}/documents/${docId}/download`, { responseType: "blob" })
    .then(r => _blobWithFilename(r, `document_${docId}`));
export const deleteEmployeeDocument = (userId: number, docId: number) =>
  api.delete(`/api/employees/${userId}/documents/${docId}`).then(r => r.data);

// ── Attendance ─────────────────────────────────────────────────────────────
export const getAttendance = (params?: { user_id?: number; month?: number; year?: number }) =>
  api.get("/api/attendance/", { params }).then(r => r.data);
export const getAttendanceSummary = (params?: { user_id?: number; month?: number; year?: number }) =>
  api.get("/api/attendance/summary", { params }).then(r => r.data);
export const markMyAttendance = (data: object) => api.post("/api/attendance/my", data).then(r => r.data);
export const punchIn = (data: { lat?: number; lng?: number; accuracy?: number; status?: string; photo?: string }) => api.post("/api/attendance/punch-in", data).then(r => r.data);
export const punchOut = (data: { lat?: number; lng?: number; accuracy?: number; photo?: string }) => api.post("/api/attendance/punch-out", data).then(r => r.data);
export const getAttendanceConfig = () => api.get("/api/attendance/config").then(r => r.data);

// Shifts (shift-management spec)
export const getShifts = (includeInactive?: boolean) => api.get("/api/shifts/", { params: includeInactive ? { include_inactive: true } : {} }).then(r => r.data);
export const createShift = (data: object) => api.post("/api/shifts/", data).then(r => r.data);
export const updateShift = (id: number, data: object) => api.put(`/api/shifts/${id}`, data).then(r => r.data);
export const deleteShift = (id: number) => api.delete(`/api/shifts/${id}`).then(r => r.data);
export const getShiftRoster = (params: { start: string; end: string; user_id?: number }) => api.get("/api/shifts/roster", { params }).then(r => r.data);
export const assignShiftRoster = (data: { user_ids: number[]; shift_id: number | null; start_date: string; end_date: string; weekdays?: number[] }) => api.post("/api/shifts/roster/assign", data).then(r => r.data);
// Cookie auth rides on <img src> requests (same pattern as employee avatars)
export const getPunchPhotoUrl = (userId: number, date: string, phase: "in" | "out") =>
  `${api.defaults.baseURL}/api/attendance/punch-photo?user_id=${userId}&date=${date}&phase=${phase}`;
export const upsertAttendance = (data: object) => api.post("/api/attendance/", data).then(r => r.data);

// Timesheet
export const getMyTimesheetEntries = (params?: { date?: string; start_date?: string; end_date?: string }) => api.get("/api/timesheet/my", { params }).then(r => r.data);
export const getMyWeekStatus = (week_start: string) => api.get("/api/timesheet/my/week-status", { params: { week_start } }).then(r => r.data);
export const submitTimesheetWeek = (week_start: string, notes?: string) => api.post("/api/timesheet/my/submit", { week_start, notes }).then(r => r.data);
export const getTimesheetApprovals = () => api.get("/api/timesheet/approvals").then(r => r.data);
export const approveTimesheetWeek = (week_id: number) => api.post(`/api/timesheet/weeks/${week_id}/approve`).then(r => r.data);
export const rejectTimesheetWeek = (week_id: number, reason: string) => api.post(`/api/timesheet/weeks/${week_id}/reject`, { reason }).then(r => r.data);
export const addTimesheetEntry = (data: object) => api.post("/api/timesheet/my", data).then(r => r.data);
export const updateTimesheetEntry = (id: number, data: object) => api.put(`/api/timesheet/my/${id}`, data).then(r => r.data);
export const deleteTimesheetEntry = (id: number) => api.delete(`/api/timesheet/my/${id}`).then(r => r.data);
export const getTeamTimesheetEntries = (params?: { date?: string; start_date?: string; end_date?: string; user_id?: number }) => api.get("/api/timesheet/team", { params }).then(r => r.data);
export const bulkUpsertAttendance = (data: object) => api.post("/api/attendance/bulk", data).then(r => r.data);
export const deleteAttendance = (id: number) => api.delete(`/api/attendance/${id}`).then(r => r.data);
export const getAttendanceAnalytics = () => api.get("/api/attendance/analytics").then(r => r.data);
export const getOnLeave = (date: string) => api.get("/api/attendance/on-leave", { params: { date } }).then(r => r.data);
export const getRegularizations = () => api.get("/api/attendance/regularize").then(r => r.data);
export const submitRegularization = (data: object) => api.post("/api/attendance/regularize", data).then(r => r.data);
export const reviewRegularization = (id: number, action: "approve" | "reject") => api.patch(`/api/attendance/regularize/${id}`, { action }).then(r => r.data);

// ── Expenses ───────────────────────────────────────────────────────────────
export const getTeamExpenses = (params?: { status?: string }) =>
  api.get("/api/expenses/team", { params }).then(r => r.data);
export const getExpenses = (params?: { status?: string; user_id?: number }) =>
  api.get("/api/expenses/", { params }).then(r => r.data);
export const submitExpense = (form: FormData) => api.post("/api/expenses/", form).then(r => r.data);
export const approveExpense = (id: number, data?: object) => api.put(`/api/expenses/${id}/approve`, data || {}).then(r => r.data);
export const rejectExpense = (id: number, data?: object) => api.put(`/api/expenses/${id}/reject`, data || {}).then(r => r.data);
export const markExpensePaid = (id: number) => api.put(`/api/expenses/${id}/paid`).then(r => r.data);
export const deleteExpense = (id: number) => api.delete(`/api/expenses/${id}`).then(r => r.data);
export const downloadReceipt = (id: number) =>
  api.get(`/api/expenses/${id}/receipt`, { responseType: "blob" })
    .then(r => _blobWithFilename(r, `receipt_${id}`));

// ── Finance: Vendors ─────────────────────────────────────────────────────────
export const getVendors = (params?: { is_active?: boolean }) =>
  api.get("/api/finance/vendors/", { params }).then(r => r.data);
export const getVendor = (id: number) =>
  api.get(`/api/finance/vendors/${id}`).then(r => r.data);
export const createVendor = (data: object) =>
  api.post("/api/finance/vendors/", data).then(r => r.data);
export const updateVendor = (id: number, data: object) =>
  api.put(`/api/finance/vendors/${id}`, data).then(r => r.data);
export const deactivateVendor = (id: number) =>
  api.put(`/api/finance/vendors/${id}/deactivate`).then(r => r.data);
export const reactivateVendor = (id: number) =>
  api.put(`/api/finance/vendors/${id}/reactivate`).then(r => r.data);
export const getVendorStatement = (id: number, params?: { from?: string; to?: string }) =>
  api.get(`/api/finance/vendors/${id}/statement`, { params }).then(r => r.data);

// ── Finance: Vendor Bills ────────────────────────────────────────────────────
export type VendorBillListResponse = {
  items: any[]; total: number; page: number; page_size: number; total_pages: number;
  currencies: string[];
};
export const getVendorBills = (params?: {
  vendor_id?: number; status?: string; period?: string; is_vat_claimable?: boolean;
  date_from?: string; date_to?: string; currency?: string;
  search?: string; sort_by?: string; sort_dir?: "asc" | "desc";
  page?: number; page_size?: number;
}) =>
  api.get("/api/finance/bills/", { params }).then(r => r.data as any[] | VendorBillListResponse);
export const getVendorBill = (id: number) =>
  api.get(`/api/finance/bills/${id}`).then(r => r.data);
export const createVendorBill = (form: FormData) =>
  api.post("/api/finance/bills/", form).then(r => r.data);
export const ocrVendorBillReceipt = (file: File) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post("/api/finance/bills/ocr-receipt", fd).then(r => r.data);
};
export const bulkOcrUpload = (files: File[]) => {
  const fd = new FormData();
  files.forEach(f => fd.append("files", f));
  return api.post("/api/finance/bills/ocr-receipt/bulk", fd).then(r => r.data);
};
export const listOcrQueue = () =>
  api.get("/api/finance/bills/ocr-receipt/bulk").then(r => r.data);
export const confirmOcrItem = (itemId: number, data: object) =>
  api.post(`/api/finance/bills/ocr-receipt/bulk/${itemId}/confirm`, data).then(r => r.data);
export const discardOcrItem = (itemId: number) =>
  api.delete(`/api/finance/bills/ocr-receipt/bulk/${itemId}`).then(r => r.data);
export const updateVendorBill = (id: number, data: object) =>
  api.put(`/api/finance/bills/${id}`, data).then(r => r.data);
export const submitVendorBill = (id: number) =>
  api.put(`/api/finance/bills/${id}/submit`).then(r => r.data);
export const approveVendorBill = (id: number) =>
  api.put(`/api/finance/bills/${id}/approve`).then(r => r.data);
export const bulkApproveVendorBills = (billIds: number[]) =>
  api.post("/api/finance/bills/bulk-approve", { bill_ids: billIds }).then(r => r.data);
export const payVendorBill = (id: number, data: { payment_method: string; payment_reference: string }) =>
  api.put(`/api/finance/bills/${id}/pay`, data).then(r => r.data);
export const voidVendorBill = (id: number) =>
  api.put(`/api/finance/bills/${id}/void`).then(r => r.data);
export const downloadVendorBillReceipt = (id: number) =>
  api.get(`/api/finance/bills/${id}/receipt`, { responseType: "blob" })
    .then(r => _blobWithFilename(r, `bill_receipt_${id}`));
export const getVendorBillVatSummary = (period: string) =>
  api.get("/api/finance/bills/vat-summary", { params: { period } }).then(r => r.data);
export const getUpcomingRecurringBills = (days = 30) =>
  api.get("/api/finance/bills/upcoming-recurring", { params: { days } }).then(r => r.data);
export const downloadVendorBillWhtCertificate = (id: number) =>
  api.get(`/api/finance/bills/${id}/wht-certificate`, { responseType: "blob" })
    .then(r => _blobWithFilename(r, `wht_certificate_${id}`));
export const downloadPp30Export = (period: string) =>
  api.get("/api/finance/bills/pp30-export", { params: { period }, responseType: "blob" })
    .then(r => _blobWithFilename(r, `PP30_${period}.csv`));
export const downloadPndExport = (period: string, formType: "PND3" | "PND53") =>
  api.get("/api/finance/bills/pnd-export", { params: { period, form_type: formType }, responseType: "blob" })
    .then(r => _blobWithFilename(r, `${formType}_${period}.csv`));
export const downloadVendorBillsExport = (params?: { status?: string; vendor_id?: number; date_from?: string; date_to?: string; currency?: string; period?: string }) =>
  api.get("/api/finance/bills/export", { params, responseType: "blob" })
    .then(r => _blobWithFilename(r, "VendorBills_export.csv"));
export const downloadVendorBillsPdf = (params?: { status?: string; vendor_id?: number; date_from?: string; date_to?: string; currency?: string; period?: string }) =>
  api.get("/api/finance/bills/export-pdf", { params, responseType: "blob" })
    .then(r => _blobWithFilename(r, "vendor_bills_report.pdf"));

// ── AI Expense Capture (AutoKey-style OCR receipt scanning) ────────────────
export type ExpenseCaptureListResponse = {
  items: any[]; has_pending: boolean; total: number;
  page?: number; page_size?: number; total_pages?: number;
};
export const getExpenseCaptures = (params?: {
  status?: string; category?: string; date_from?: string; date_to?: string;
  branch_id?: number; tax_status?: string;
  search?: string; sort_by?: string; sort_dir?: "asc" | "desc";
  page?: number; page_size?: number;
  ocr_pending?: boolean; batch_id?: string; watching?: boolean;
}) => api.get("/api/expense-captures/", { params }).then(r => r.data as ExpenseCaptureListResponse);
export const scanExpenseCaptureReceiptAsync = (files: File[], batchId: string, source?: string) => {
  const fd = new FormData();
  files.forEach(f => fd.append("files", f));
  fd.append("batch_id", batchId);
  if (source) fd.append("source", source);
  return api.post("/api/expense-captures/scan-async", fd).then(r => r.data as { batch_id: string; captures: any[] });
};
export const getExpenseCapture = (id: number) =>
  api.get(`/api/expense-captures/${id}`).then(r => r.data);
export const scanExpenseCaptureReceipt = (file: File) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post("/api/expense-captures/scan", fd).then(r => r.data);
};
export const createExpenseCapture = (form: FormData) =>
  api.post("/api/expense-captures/", form).then(r => r.data);
export const updateExpenseCapture = (id: number, form: FormData) =>
  api.put(`/api/expense-captures/${id}`, form).then(r => r.data);
export const deleteExpenseCapture = (id: number) =>
  api.delete(`/api/expense-captures/${id}`).then(r => r.data);
export const confirmExpenseCapture = (id: number) =>
  api.post(`/api/expense-captures/${id}/confirm`).then(r => r.data);
export const rejectExpenseCapture = (id: number) =>
  api.post(`/api/expense-captures/${id}/reject`).then(r => r.data);
export const reprocessExpenseCapture = (id: number) =>
  api.post(`/api/expense-captures/${id}/reprocess`).then(r => r.data);
export const resolveDuplicateExpenseCapture = (id: number, action: "keep_both" | "discard") => {
  const fd = new FormData();
  fd.append("action", action);
  return api.post(`/api/expense-captures/${id}/resolve-duplicate`, fd).then(r => r.data);
};
export const getExpenseCaptureSplits = (id: number) =>
  api.get(`/api/expense-captures/${id}/splits`).then(r => r.data as { splits: { branch_id: number; branch_name: string | null; amount: number }[] });
export const setExpenseCaptureSplits = (id: number, splits: { branch_id: number; amount: number }[]) =>
  api.put(`/api/expense-captures/${id}/splits`, splits).then(r => r.data);

// ── Branches (expense-capture-native-v2 Phase 2) ─────────────────────────────
export const getBranches = () =>
  api.get("/api/branches/").then(r => r.data as { branches: { id: number; name: string; tax_branch_no: string | null; address: string | null; status: string; employee_count: number }[] });
export const createBranch = (data: { name: string; tax_branch_no?: string; address?: string }) =>
  api.post("/api/branches/", data).then(r => r.data);
export const updateBranch = (id: number, data: Partial<{ name: string; tax_branch_no: string; address: string; status: string }>) =>
  api.patch(`/api/branches/${id}`, data).then(r => r.data);
export const getBranchesConfig = () =>
  api.get("/api/branches/config").then(r => r.data as { enabled: boolean });
export const setBranchesConfig = (enabled: boolean) =>
  api.put("/api/branches/config", { enabled }).then(r => r.data as { enabled: boolean });
export const bulkAssignHistoricalBranch = (branch_id: number) =>
  api.post("/api/branches/wizard/bulk-assign", { branch_id }).then(r => r.data as { captures_updated: number });
export const getBranchEmployees = () =>
  api.get("/api/branches/employees").then(r => r.data as { employees: { id: number; name: string; email: string; role: string; branch_id: number | null; granted_branch_ids: number[] }[] });
export const setEmployeeHomeBranch = (userId: number, branch_id: number | null) =>
  api.put(`/api/branches/employees/${userId}`, { branch_id }).then(r => r.data);
export const grantEmployeeBranch = (userId: number, branch_id: number) =>
  api.post(`/api/branches/employees/${userId}/grants`, { branch_id }).then(r => r.data);
export const revokeEmployeeBranch = (userId: number, branchId: number) =>
  api.delete(`/api/branches/employees/${userId}/grants/${branchId}`).then(r => r.data);

// ── Departments (department-master-data) ─────────────────────────────────────
export interface DepartmentRow { id: number; name: string; status: string; employee_count: number; }
export const getDepartments = () =>
  api.get("/api/departments/").then(r => r.data as { departments: DepartmentRow[] });
export const createDepartment = (name: string) =>
  api.post("/api/departments/", { name }).then(r => r.data as DepartmentRow);
export const updateDepartment = (id: number, data: Partial<{ name: string; status: string }>) =>
  api.patch(`/api/departments/${id}`, data).then(r => r.data as DepartmentRow);
export const mergeDepartment = (id: number, target_id: number) =>
  api.post(`/api/departments/${id}/merge`, { target_id }).then(r => r.data as { merged_into: DepartmentRow; employees_moved: number });
export const deleteDepartment = (id: number) =>
  api.delete(`/api/departments/${id}`).then(r => r.data);
export const getDepartmentMergeSuggestions = () =>
  api.get("/api/departments/suggestions").then(r => r.data as { suggestions: { keep: DepartmentRow; merge: DepartmentRow }[] });
export const dismissDepartmentMergeSuggestion = (department_ids: [number, number]) =>
  api.post("/api/departments/suggestions/dismiss", { department_ids }).then(r => r.data);
export const downloadExpenseCapturesExport = (params?: { status?: string; category?: string; date_from?: string; date_to?: string; branch_id?: number; tax_status?: string }) =>
  api.get("/api/expense-captures/export-csv", { params, responseType: "blob" })
    .then(r => _blobWithFilename(r, "expense_captures.csv"));
export const downloadExpenseCapturesPdf = (params?: { status?: string; category?: string; date_from?: string; date_to?: string; branch_id?: number; tax_status?: string }) =>
  api.get("/api/expense-captures/export-pdf", { params, responseType: "blob" })
    .then(r => _blobWithFilename(r, "expense_report.pdf"));
export const fetchExpenseCaptureReceiptBlob = (id: number) =>
  api.get(`/api/expense-captures/${id}/receipt`, { responseType: "blob" }).then(r => r.data as Blob);
export const fetchExpenseCaptureReceiptUrl = (id: number) =>
  api.get(`/api/expense-captures/${id}/receipt-url`).then(r => r.data as { url: string });

// ── Ledger & bulk confirm (Phase 3) ──────────────────────────────────────────
export type CurrencyTotal = { currency: string; currency_symbol: string; total: number };
export const getLedgerSummary = (params?: { month?: string; branch_id?: number }) =>
  api.get("/api/expense-captures/ledger/summary", { params }).then(r => r.data as {
    period: string | null; vat_reclaimable_by_currency: CurrencyTotal[]; vat_reclaimable_count: number;
    wht_owed_by_currency: CurrencyTotal[]; wht_owed_count: number;
  });
export const downloadExpenseBundle = (params?: { month?: string; branch_id?: number }) =>
  api.get("/api/expense-captures/export-bundle", { params, responseType: "blob" })
    .then(r => _blobWithFilename(r, "expense_bundle.zip"));
export const getBulkConfirmEligible = () =>
  api.get("/api/expense-captures/bulk-confirm/eligible").then(r => r.data as { captures: any[] });
export const bulkConfirmExpenseCaptures = (captureIds?: number[]) =>
  api.post("/api/expense-captures/bulk-confirm", { capture_ids: captureIds ?? null })
    .then(r => r.data as { confirmed_ids: number[]; count: number });

// ── Bulk actions on an explicit selection (distinct from the auto-eligibility macro
// above) — acts on exactly the capture_ids passed, regardless of confidence score.
export type BulkActionResult = { results: { id: number; ok: boolean; reason: string | null }[] };
export const bulkActionConfirmExpenseCaptures = (captureIds: number[]) =>
  api.post("/api/expense-captures/bulk-actions/confirm", { capture_ids: captureIds }).then(r => r.data as BulkActionResult);
export const bulkActionRejectExpenseCaptures = (captureIds: number[]) =>
  api.post("/api/expense-captures/bulk-actions/reject", { capture_ids: captureIds }).then(r => r.data as BulkActionResult);
export const bulkActionDeleteExpenseCaptures = (captureIds: number[]) =>
  api.post("/api/expense-captures/bulk-actions/delete", { capture_ids: captureIds }).then(r => r.data as BulkActionResult);

// ── Insights (Phase 4) ────────────────────────────────────────────────────────
export type SpendCategory = {
  category: string; amount: number; pct_of_total: number;
  pct_change_vs_last_month: number | null;
  budget: number | null; budget_pct: number | null; budget_alert: boolean;
};
// CurrencyTotal is declared with the ledger types above — same {currency, symbol, total}
// shape, since both come from the backend's shared sum_by_currency helper.
export type SpendBreakdown = {
  period: string;
  // Every figure below is denominated in this one currency. Spend in any other currency is
  // in `other_currencies` and must never be added to these — see the C5-13 note in
  // routers/expense_insights.py.
  primary_currency: string; primary_currency_symbol: string;
  total_this_month: number; total_last_month: number;
  total_pct_change: number | null; categories: SpendCategory[];
  other_currencies: CurrencyTotal[];
};
export const getSpendBreakdown = (month?: string) =>
  api.get("/api/expense-captures/insights/spend-breakdown", { params: { month } })
    .then(r => r.data as SpendBreakdown);
export type InsightsNarrative = {
  period: string; insights: string[];
  // available=false for branch-scoped users: the narrative is written from tenant-wide
  // figures and cached without a branch dimension, so it is unrestricted-scope only.
  available?: boolean; cached?: boolean; currency?: string | null; generated_at?: string | null;
};
export const getInsightsNarrative = (month?: string) =>
  api.get("/api/expense-captures/insights/narrative", { params: { month } })
    .then(r => r.data as InsightsNarrative);
export const refreshInsightsNarrative = (month?: string) =>
  api.post("/api/expense-captures/insights/narrative/refresh", null, { params: { month } })
    .then(r => r.data as InsightsNarrative);
export const getBudgets = () =>
  api.get("/api/expense-captures/insights/budgets").then(r => r.data as { budgets: { category: string; monthly_amount: number }[] });
export const setBudget = (category: string, monthly_amount: number) =>
  api.put("/api/expense-captures/insights/budgets", { category, monthly_amount }).then(r => r.data);
export const deleteBudget = (category: string) =>
  api.delete(`/api/expense-captures/insights/budgets/${encodeURIComponent(category)}`).then(r => r.data);
export type BranchCurrencyAmount = {
  currency: string; currency_symbol: string; amount: number; pct_change_vs_last_month: number | null;
};
export const getBranchComparison = (month?: string) =>
  api.get("/api/expense-captures/insights/branch-comparison", { params: { month } }).then(r => r.data as {
    period: string; branches: { branch_id: number; branch_name: string; amounts: BranchCurrencyAmount[] }[];
  });

// ── AI Expense Capture module dashboard ──────────────────────────────────────
// One call backs the whole page. Composed server-side rather than fanning out to
// spend-breakdown + ledger + branch-comparison + narrative from the client, so the page
// cannot render four panels that disagree about the period or the currency.
export type ExpenseDashboard = {
  period: string;
  primary_currency: string; primary_currency_symbol: string;
  stats: {
    needs_review: number; ready_to_confirm: number; extraction_failed: number; processing: number;
    spend_this_month: number; spend_last_month: number; spend_pct_change: number | null;
    spend_other_currencies: CurrencyTotal[];
    vat_reclaimable_by_currency: CurrencyTotal[]; vat_reclaimable_count: number;
  };
  queue: {
    id: number; vendor_name: string; total: number; currency: string;
    currency_symbol: string; status: string; created_at: string | null;
  }[];
  trend: { month: string; total: number }[];
  trend_other_currencies: CurrencyTotal[];
  budget_alerts: { category: string; amount: number; budget: number | null; budget_pct: number | null }[];
  top_branches: { branch_id: number; branch_name: string; amounts: BranchCurrencyAmount[] }[];
  narrative: InsightsNarrative;
};
export const getExpenseDashboard = () =>
  api.get("/api/expense-captures/insights/dashboard").then(r => r.data as ExpenseDashboard);

// ── Interviews ─────────────────────────────────────────────────────────────
export const getInterviews = (candidateId: number, applicationId?: number | null) =>
  api.get(`/api/candidates/${candidateId}/interviews`, {
    params: applicationId != null ? { application_id: applicationId } : undefined,
  }).then(r => r.data);
export const scheduleInterview = (applicationId: number, data: object) =>
  api.post(`/api/candidates/${applicationId}/interviews`, data).then(r => r.data);
export const updateInterview = (interviewId: number, data: object) =>
  api.put(`/api/candidates/interviews/${interviewId}`, data).then(r => r.data);
export const deleteInterview = (interviewId: number) =>
  api.delete(`/api/candidates/interviews/${interviewId}`).then(r => r.data);
export const deleteSlotOffer = (offerId: number) =>
  api.delete(`/api/candidates/slot-offers/${offerId}`).then(r => r.data);
export const proposeInterviewSlots = (applicationId: number, data: object) =>
  api.post(`/api/candidates/${applicationId}/propose-slots`, data).then(r => r.data);
export const getPublicSlotOffer = (token: string) =>
  api.get(`/api/public/interview-slots/${token}`).then(r => r.data);
export const confirmInterviewSlot = (token: string, slotIndex: number) =>
  api.post(`/api/public/interview-slots/${token}/confirm`, { slot_index: slotIndex }).then(r => r.data);
export const getInterviewerOffer = (token: string) =>
  api.get(`/api/public/interview-slots/interviewer/${token}`).then(r => r.data);
export const submitInterviewerSlots = (token: string, slots: { slot_1: string; slot_2?: string; slot_3?: string }) =>
  api.post(`/api/public/interview-slots/interviewer/${token}/submit`, slots).then(r => r.data);
export const bulkUpdateStatus = (data: { application_ids: number[]; status: string }) =>
  api.post("/api/candidates/bulk-status", data).then(r => r.data);

// ── Performance Reviews ────────────────────────────────────────────────────
export const getReviewCycles = () => api.get("/api/performance/cycles").then(r => r.data);
export const createReviewCycle = (data: object) => api.post("/api/performance/cycles", data).then(r => r.data);
export const updateReviewCycle = (id: number, data: object) => api.patch(`/api/performance/cycles/${id}`, data).then(r => r.data);
export const deleteReviewCycle = (id: number) => api.delete(`/api/performance/cycles/${id}`).then(r => r.data);
export const activateReviewCycle = (id: number) => api.post(`/api/performance/cycles/${id}/activate`).then(r => r.data);
export const getReviews = (cycleId?: number) => api.get("/api/performance/reviews", { params: cycleId ? { cycle_id: cycleId } : {} }).then(r => r.data);
export const getMyReviews = () => api.get("/api/performance/reviews/my").then(r => r.data);
export const getReviewsToAction = () => api.get("/api/performance/reviews/to-review").then(r => r.data);
export const submitSelfAssessment = (id: number, data: object) => api.patch(`/api/performance/reviews/${id}/self`, data).then(r => r.data);
export const submitManagerAssessment = (id: number, data: object) => api.patch(`/api/performance/reviews/${id}/manager`, data).then(r => r.data);

// ── Dashboard ─────────────────────────────────────────────────────────────
export const getDashboardSummary    = () => api.get("/api/dashboard/summary").then(r => r.data);
export const getDashboardActivity   = () => api.get("/api/dashboard/activity").then(r => r.data);
export const getDashboardLeaveTrend  = () => api.get("/api/dashboard/leave-trend").then(r => r.data);
export const getDashboardFinanceTrend = () => api.get("/api/dashboard/finance-trend").then(r => r.data);
export const getDashboardFinanceActivity = () => api.get("/api/dashboard/finance-activity").then(r => r.data);
export const getDashboardFinanceApSummary = (period?: string) =>
  api.get("/api/dashboard/finance-ap-summary", { params: period ? { period } : undefined }).then(r => r.data);
export const getDashboardRecruitment = () => api.get("/api/dashboard/recruitment").then(r => r.data);
export const getDashboardPeople = () => api.get("/api/dashboard/people").then(r => r.data);
export const getPayrollSummary = () => api.get("/api/payroll/summary").then(r => r.data);
export const getEsignDashboard = () => api.get("/api/esign/dashboard").then(r => r.data);
export const getPerformanceDashboard = () => api.get("/api/performance/dashboard").then(r => r.data);

// ── Year-end rollover ──────────────────────────────────────────────────────
export const yearEndRollover = (data: { from_year: number; to_year: number }) =>
  api.post("/api/leaves/year-end-rollover", data).then(r => r.data);

// ── Offer Letters ──────────────────────────────────────────────────────────
export const getOffers = () => api.get("/api/offers").then(r => r.data);
export const generateOffer = (data: object) => api.post("/api/offers/generate", data).then(r => r.data);
export const sendOffer = (id: number) => api.post(`/api/offers/${id}/send`).then(r => r.data);
export const getOffer = (id: number) => api.get(`/api/offers/${id}`).then(r => r.data);
export const updateOffer = (id: number, data: object) => api.put(`/api/offers/${id}`, data).then(r => r.data);
export const deleteOffer = (id: number) => api.delete(`/api/offers/${id}`).then(r => r.data);
export const downloadOfferPdf = async (id: number) => {
  const r = await api.get(`/api/offers/${id}/pdf`, { responseType: "blob" });
  return _blobWithFilename(r, `offer_${id}.pdf`);
};
export const getOfferApprovers = () => api.get("/api/offers/approvers").then(r => r.data);
export const submitOfferForApproval = (id: number) => api.post(`/api/offers/${id}/submit-for-approval`).then(r => r.data);
export const approveOffer = (id: number, typedName?: string, consentChecked?: boolean) =>
  api.post(`/api/offers/${id}/approve`, { typed_name: typedName || null, consent_checked: !!consentChecked }).then(r => r.data);
export const rejectOffer = (id: number, reason: string) => api.post(`/api/offers/${id}/reject`, { reason }).then(r => r.data);
export const getOfferSignatureEvents = (id: number) => api.get(`/api/offers/${id}/signature-events`).then(r => r.data);
export const downloadOfferCertificate = async (id: number) => {
  const r = await api.get(`/api/offers/${id}/certificate`, { responseType: "blob" });
  return _blobWithFilename(r, `certificate_${id}.pdf`);
};

// ── HR Letters ─────────────────────────────────────────────────────────────
export const getLetterTypes = () => api.get("/api/letters/types").then(r => r.data);
export const getLetters = (employeeId?: number) =>
  api.get("/api/letters/", { params: employeeId ? { employee_id: employeeId } : {} }).then(r => r.data);
export const generateLetter = (data: object) => api.post("/api/letters/generate", data).then(r => r.data);
export const updateLetter = (id: number, data: object) => api.put(`/api/letters/${id}`, data).then(r => r.data);
export const deleteLetter = (id: number) => api.delete(`/api/letters/${id}`).then(r => r.data);
export const downloadLetterPdf = async (id: number) => {
  const r = await api.get(`/api/letters/${id}/pdf`, { responseType: "blob" });
  return _blobWithFilename(r, `letter_${id}.pdf`);
};
export const requestLetterSignature = (id: number) => api.post(`/api/letters/${id}/request-signature`).then(r => r.data);
export const getLetterSignature = (id: number) => api.get(`/api/letters/${id}/signature`).then(r => r.data);
export const signLetter = (id: number, typedName: string, signatureDrawn: string | null, consentChecked: boolean) =>
  api.post(`/api/letters/${id}/sign`, { typed_name: typedName, signature_drawn: signatureDrawn, consent_checked: consentChecked }).then(r => r.data);
export const declineLetterSignature = (id: number, reason: string) =>
  api.post(`/api/letters/${id}/decline`, { reason }).then(r => r.data);
export const getLetterSignatureEvents = (id: number) => api.get(`/api/letters/${id}/signature-events`).then(r => r.data);

// ── Letter Self-Requests ───────────────────────────────────────────────────
export const getMyLetters = () => api.get("/api/letters/my").then(r => r.data);
export const getRequestableLetterTypes = () => api.get("/api/letters/requestable-types").then(r => r.data);
export const createLetterRequest = (data: object) => api.post("/api/letters/requests", data).then(r => r.data);
export const getMyLetterRequests = () => api.get("/api/letters/requests/my").then(r => r.data);
export const getLetterRequests = (status?: string) =>
  api.get("/api/letters/requests", { params: status ? { status } : {} }).then(r => r.data);
export const approveLetterRequest = (id: number, params?: object) =>
  api.post(`/api/letters/requests/${id}/approve`, params ? { params } : {}).then(r => r.data);
export const rejectLetterRequest = (id: number, reason: string) =>
  api.post(`/api/letters/requests/${id}/reject`, { reason }).then(r => r.data);

// ── Document Signing Module ─────────────────────────────────────────────────
// Upload-any-PDF e-signature, separate from Offers/Letters.
// See .claude/specs/document-signing-module.md.
export const getEsignTemplates = () => api.get("/api/esign/templates").then(r => r.data);
export const getEsignTemplate = (id: number) => api.get(`/api/esign/templates/${id}`).then(r => r.data);
export const createEsignTemplate = (
  file: File, name: string, fields: object[], roles: object[], isReusable: boolean,
) => {
  const form = new FormData();
  form.append("file", file);
  form.append("name", name);
  form.append("fields", JSON.stringify(fields));
  form.append("roles", JSON.stringify(roles));
  form.append("is_reusable", String(isReusable));
  return api.post("/api/esign/templates", form, { headers: { "Content-Type": "multipart/form-data" } }).then(r => r.data);
};
export const deleteEsignTemplate = (id: number) => api.delete(`/api/esign/templates/${id}`).then(r => r.data);

export const createEsignRequest = (data: {
  template_id: number; title: string;
  recipients: { role: string; internal_user_id?: number; external_name?: string; external_email?: string }[];
  reminder_cadence_days?: number; expires_at?: string;
}) => api.post("/api/esign/requests", data).then(r => r.data);
export const getEsignRequests = () => api.get("/api/esign/requests").then(r => r.data);
export const getEsignRequest = (id: number) => api.get(`/api/esign/requests/${id}`).then(r => r.data);
export const downloadEsignRequestPdf = async (id: number) => {
  const r = await api.get(`/api/esign/requests/${id}/pdf`, { responseType: "blob" });
  return _blobWithFilename(r, `document_${id}.pdf`);
};
export const voidEsignRequest = (id: number) => api.post(`/api/esign/requests/${id}/void`).then(r => r.data);
export const getEsignRecipientView = (requestId: number, recipientId: number) =>
  api.get(`/api/esign/requests/${requestId}/recipients/${recipientId}`).then(r => r.data);
export const signEsignRequestInternal = (
  requestId: number, recipientId: number, typedName: string, signatureDrawn: string | null, consentChecked: boolean,
) => api.post(`/api/esign/requests/${requestId}/recipients/${recipientId}/sign`,
  { typed_name: typedName, signature_drawn: signatureDrawn, consent_checked: consentChecked }).then(r => r.data);
export const declineEsignRequestInternal = (requestId: number, recipientId: number, reason: string) =>
  api.post(`/api/esign/requests/${requestId}/recipients/${recipientId}/decline`, { reason }).then(r => r.data);

export const bulkSendEsign = (templateId: number, data: {
  title: string;
  recipients: { internal_user_id?: number; external_name?: string; external_email?: string }[];
  reminder_cadence_days?: number; expires_at?: string;
}) => api.post(`/api/esign/templates/${templateId}/bulk-send`, data).then(r => r.data);
export const getEsignBatches = () => api.get("/api/esign/batches").then(r => r.data);
export const getEsignBatch = (batchId: string) => api.get(`/api/esign/batches/${batchId}`).then(r => r.data);
export const getMyEsignTasks = () => api.get("/api/esign/my-tasks").then(r => r.data);

// ── Referrals ──────────────────────────────────────────────────────────────
export const getMyReferrals = () => api.get("/api/employees/me/referrals").then(r => r.data);

// ── Announcements ──────────────────────────────────────────────────────────
export const getAnnouncements = (params = "") => api.get(`/api/announcements/${params}`).then(r => r.data);
export const getAnnouncementUnreadCount = () => api.get("/api/announcements/unread-count").then(r => r.data);
export const createAnnouncement = (data: object) => api.post("/api/announcements/", data).then(r => r.data);
export const updateAnnouncement = (id: number, data: object) => api.put(`/api/announcements/${id}`, data).then(r => r.data);
export const deleteAnnouncement = (id: number) => api.delete(`/api/announcements/${id}`).then(r => r.data);
export const markAnnouncementRead = (id: number) => api.post(`/api/announcements/${id}/read`).then(r => r.data);
export const aiWriteAnnouncement = (data: { topic: string; tone: string; category: string }) =>
  api.post("/api/announcements/ai-write", data).then(r => r.data);

// ── Helpdesk ───────────────────────────────────────────────────────────────
export const createTicket = (data: object) => api.post("/api/helpdesk/", data).then(r => r.data);
export const getMyTickets = () => api.get("/api/helpdesk/my").then(r => r.data);
export const getAllTickets = (params?: object) => api.get("/api/helpdesk/", { params }).then(r => r.data);
export const getTicket = (id: number) => api.get(`/api/helpdesk/${id}`).then(r => r.data);
export const addTicketComment = (id: number, body: string) => api.post(`/api/helpdesk/${id}/comments`, { body }).then(r => r.data);
export const updateTicket = (id: number, data: object) => api.put(`/api/helpdesk/${id}`, data).then(r => r.data);

// ── Pulse Survey ───────────────────────────────────────────────────────────
export const getPulseStatus = () => api.get("/api/pulse/status").then(r => r.data);
export const submitPulse = (data: { score: number; comment?: string }) => api.post("/api/pulse/respond", data).then(r => r.data);
export const getPulseResults = () => api.get("/api/pulse/results").then(r => r.data);
export const setPulseQuestion = (question: string) => api.put("/api/pulse/question", { question }).then(r => r.data);

// ── Exit Management ────────────────────────────────────────────────────────
export const getExitRequests = () => api.get("/api/exits/").then(r => r.data);
export const submitExitRequest = (data: object) => api.post("/api/exits/", data).then(r => r.data);
export const updateExitRequest = (id: number, data: object) => api.patch(`/api/exits/${id}`, data).then(r => r.data);
export const cancelExitRequest = (id: number) => api.delete(`/api/exits/${id}`).then(r => r.data);

// ── Assets ─────────────────────────────────────────────────────────────────
export const getAssets = (params?: object) => api.get("/api/assets/", { params }).then(r => r.data);
export const createAsset = (data: object) => api.post("/api/assets/", data).then(r => r.data);
export const updateAsset = (id: number, data: object) => api.patch(`/api/assets/${id}`, data).then(r => r.data);
export const deleteAsset = (id: number) => api.delete(`/api/assets/${id}`).then(r => r.data);

// ── Reports ────────────────────────────────────────────────────────────────
// The pre-shell snapshot endpoints (/api/reports/headcount, /payroll-cost,
// /leave-utilization, /my-leave-summary) no longer have a frontend caller —
// every report moved onto the v2 contract below. Their client functions are
// gone rather than left as dead exports; the endpoints themselves survive one
// release in case anything outside this app is calling them.

// ── Reports v2 · the shared report contract ────────────────────────────────
// Spec: .claude/specs/reports-v2-shell-and-people-analytics.md
// Every report takes the same four inputs and returns the same envelope, so
// ReportShell renders any of them without knowing which one it is.
export type ReportName =
  | "headcount" | "headcount-movement" | "attrition" | "org-structure"
  | "leave-utilization" | "leave-liability" | "absenteeism" | "payroll-cost"
  | "recruitment-funnel" | "time-to-hire" | "source-effectiveness"
  | "interviews" | "offers" | "pipeline-health" | "ai-screening";

export type ReportQuery = {
  period: string;
  start?: string | null;
  end?: string | null;
  compare: "none" | "previous" | "last_year";
  group_by?: string | null;
  // Leave Liability only: which salary basis to value unused leave at.
  basis?: "basic" | "gross";
};

export type ReportFigure = {
  key: string; label: string; value: number | null;
  format: "number" | "money" | "percent" | "days";
  good: "up" | "down" | "neutral";
  currency_symbol: string | null; currency: string | null; sub: string | null;
  previous: number | null; delta: number | null; delta_pct: number | null;
  direction: "up" | "down" | "flat" | null;
};

export type ReportBreakdownRow = {
  key: string; label: string; value: number; previous: number | null;
  currency_symbol?: string | null; currency?: string | null;
  color?: string | null; allocated?: number; employee_count?: number; utilization_pct?: number;
};

export type ReportEnvelope = {
  report: string;
  period: { start: string; end: string; label: string };
  comparison: { start: string; end: string; label: string } | null;
  group_by: string;
  supports_group_by: string[];
  figures: ReportFigure[];
  breakdowns: { label: string; group_by: string; rows: ReportBreakdownRow[] }[];
  series: { currency: string; currency_symbol: string; points: { month: string; total: number; headcount: number }[] }[];
  notes: string[];
  unavailable_module?: string | null;
};

export const getReport = (report: ReportName, q: ReportQuery): Promise<ReportEnvelope> =>
  api.get(`/api/reports/v2/${report}`, { params: q }).then(r => r.data);

export const getReportRows = (report: ReportName, q: ReportQuery, bucket: string) =>
  api.get(`/api/reports/v2/${report}/rows`, { params: { ...q, bucket } }).then(r => r.data);

// _downloadBlob is declared further down; this only dereferences it at call
// time, so the ordering is fine.
export const downloadReportPdf = (report: ReportName, q: ReportQuery) =>
  _downloadBlob(`/api/reports/v2/${report}/export`, q, `${report}.pdf`);

// ── In-app Notifications ───────────────────────────────────────────────────
export const getNotifications = () => api.get("/api/notifications/").then(r => r.data);
export const getUnreadCount = () => api.get("/api/notifications/unread-count").then(r => r.data);
export const markNotificationRead = (id: number) => api.post(`/api/notifications/${id}/read`).then(r => r.data);
export const markAllNotificationsRead = () => api.post("/api/notifications/read-all").then(r => r.data);

export const getPreferredCompanies = (): Promise<{ companies: string[] }> =>
  api.get("/api/settings/preferred-companies").then(r => r.data);
export const savePreferredCompanies = (companies: string[]): Promise<{ companies: string[] }> =>
  api.put("/api/settings/preferred-companies", { companies }).then(r => r.data);
export const resetPreferredCompanies = (): Promise<{ companies: string[] }> =>
  api.put("/api/settings/preferred-companies", { companies: [], reset: true }).then(r => r.data);

// ── Recruiter Preferences (scoring/screening defaults + notify pref) ────────
export type ScoringDefaults = {
  role_type: string;
  knockouts: { type: string; value: string }[];
  red_flags: Record<string, boolean>;
};
export type ScreeningDefaults = {
  question_count: number;
  auto_invite_threshold: number;
  question_bank: { role_family: string; question: string }[];
};
// email_available is true when mail will actually go out by EITHER route — the tenant's own
// SMTP or the platform relay it falls back to. Not the same as "this tenant configured SMTP".
export type ApplicantNotifyPref = { preference: "instant" | "digest" | "off"; email_available: boolean };
// Application Screening Question templates (spec: application-question-templates) —
// distinct from ScreeningDefaults.question_bank above, which is the unrelated AI
// Video Screening question bank.
export type QuestionTemplate = {
  id: string; label: string; field_type: QuestionFieldType; options: string[]; required: boolean;
};
export type QuestionTemplates = { templates: QuestionTemplate[] };

export const getScoringDefaults = (): Promise<ScoringDefaults> =>
  api.get("/api/settings/scoring-defaults").then(r => r.data);
export const saveScoringDefaults = (data: ScoringDefaults | { reset: true }): Promise<ScoringDefaults> =>
  api.put("/api/settings/scoring-defaults", data).then(r => r.data);
export const getScreeningDefaults = (): Promise<ScreeningDefaults> =>
  api.get("/api/settings/screening-defaults").then(r => r.data);
export const saveScreeningDefaults = (data: ScreeningDefaults | { reset: true }): Promise<ScreeningDefaults> =>
  api.put("/api/settings/screening-defaults", data).then(r => r.data);
export const getQuestionTemplates = (): Promise<QuestionTemplates> =>
  api.get("/api/settings/question-templates").then(r => r.data);
export const saveQuestionTemplates = (data: QuestionTemplates | { reset: true }): Promise<QuestionTemplates> =>
  api.put("/api/settings/question-templates", data).then(r => r.data);
export const getApplicantNotifyPref = (): Promise<ApplicantNotifyPref> =>
  api.get("/api/settings/applicant-notifications").then(r => r.data);
export const saveApplicantNotifyPref = (preference: string): Promise<ApplicantNotifyPref> =>
  api.put("/api/settings/applicant-notifications", { preference }).then(r => r.data);

// ── Payroll compliance reports ─────────────────────────────────────────────
const _downloadBlob = async (url: string, params: object, defaultFilename: string) => {
  const r = await api.get(url, { params, responseType: "blob" });
  const filename = _filenameFromContentDisposition(r.headers["content-disposition"] || "", defaultFilename);
  const blob = new Blob([r.data]);
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
};

export const downloadPnd1Report = (month: number, year: number) =>
  _downloadBlob("/api/payroll/reports/pnd1", { month, year }, `PND1_${month}_${year}.csv`);
export const downloadSso10Report = (month: number, year: number) =>
  _downloadBlob("/api/payroll/reports/sso10", { month, year }, `SSO10_${month}_${year}.csv`);
export const downloadAnnualSummary = (year: number) =>
  _downloadBlob("/api/payroll/reports/annual-summary", { year }, `AnnualPayrollSummary_${year}.csv`);
export const downloadBankFile = (month: number, year: number, bank_format: string) =>
  _downloadBlob("/api/payroll/reports/bank-file", { month, year, bank_format }, `BankFile_${month}_${year}.csv`);
export const downloadForm16 = (userId: number, fy_year: number) =>
  _downloadBlob(`/api/payroll/employees/${userId}/form16`, { fy_year }, `Form16_${userId}_FY${fy_year}.pdf`);
export const downloadWhtCertificate = (userId: number, year: number) =>
  _downloadBlob(`/api/payroll/employees/${userId}/wht-certificate`, { year }, `WHTCert_${userId}_${year}.pdf`);
export const getPtStates = () => api.get("/api/payroll/pt-states").then(r => r.data);

// ── AI Video Screening ─────────────────────────────────────────────────────
export const listScreeningTemplates = () =>
  api.get("/api/screening/templates").then(r => r.data);
export const generateScreeningQuestions = (jobId: number) =>
  api.post(`/api/screening/templates/${jobId}/generate`).then(r => r.data);
export const getScreeningTemplate = (jobId: number) =>
  api.get(`/api/screening/templates/${jobId}`).then(r => r.data);
export const saveScreeningTemplate = (jobId: number, data: object) =>
  api.post(`/api/screening/templates/${jobId}`, data).then(r => r.data);
export const listScreeningSessions = (jobId?: number) =>
  api.get("/api/screening/sessions", { params: jobId ? { job_id: jobId } : {} }).then(r => r.data);
export const exportScreeningSessions = async (jobId?: number): Promise<void> => {
  const params = jobId ? `?job_id=${jobId}` : "";
  const res = await api.get(`/api/screening/sessions/export${params}`, { responseType: "blob" });
  const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = jobId ? `screening-sessions-job${jobId}.csv` : "screening-sessions-all.csv";
  a.click();
  URL.revokeObjectURL(url);
};
export const getScreeningSession = (id: number) =>
  api.get(`/api/screening/sessions/${id}`).then(r => r.data);
export const createScreeningSession = (data: object) =>
  api.post("/api/screening/sessions", data).then(r => r.data);
export const deleteScreeningSession = (id: number) =>
  api.delete(`/api/screening/sessions/${id}`).then(r => r.data);
export const rescoreScreeningSession = (id: number) =>
  api.post(`/api/screening/sessions/${id}/rescore`).then(r => r.data);
export const resendScreeningSession = (id: number) =>
  api.post(`/api/screening/sessions/${id}/resend`).then(r => r.data);
export const getScreeningVideoUrl = (sessionId: number, answerId: number) =>
  `${process.env.REACT_APP_API_URL || "http://localhost:30801"}/api/screening/sessions/${sessionId}/video/${answerId}`;

// Public (no auth) — use raw fetch with credentials for token-based endpoints
const PUBLIC_API = process.env.REACT_APP_API_URL || "http://localhost:30801";
export const getPublicScreen = (token: string) =>
  fetch(`${PUBLIC_API}/api/public/screen/${token}`, { credentials: "include" }).then(r => { if (!r.ok) throw r; return r.json(); });
export const startPublicScreen = (token: string) =>
  fetch(`${PUBLIC_API}/api/public/screen/${token}/start`, { method: "POST", credentials: "include" }).then(r => { if (!r.ok) throw r; return r.json(); });
export const submitScreeningAnswer = async (token: string, questionId: number, videoBlob: Blob, durationSeconds: number, tabSwitches: number = 0) => {
  const fd = new FormData();
  fd.append("video", videoBlob, videoBlob.type.includes("mp4") ? "answer.mp4" : "answer.webm");
  fd.append("duration_seconds", String(Math.round(durationSeconds)));
  fd.append("tab_switches", String(tabSwitches));
  const r = await fetch(`${PUBLIC_API}/api/public/screen/${token}/answer/${questionId}`, {
    method: "POST", credentials: "include", body: fd,
  });
  if (!r.ok) throw r;
  return r.json();
};
export const completePublicScreen = (token: string) =>
  fetch(`${PUBLIC_API}/api/public/screen/${token}/complete`, { method: "POST", credentials: "include" }).then(r => { if (!r.ok) throw r; return r.json(); });
export const markQuestionSeen = (token: string, questionId: number) =>
  fetch(`${PUBLIC_API}/api/public/screen/${token}/seen/${questionId}`, { method: "POST", credentials: "include" }).catch(() => {/* best-effort */});
export const heartbeatPublicScreen = (token: string) =>
  fetch(`${PUBLIC_API}/api/public/screen/${token}/heartbeat`, { method: "POST", credentials: "include" }).catch(() => {/* best-effort */});

export default api;
